import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import type { LayerProps, MapboxGeoJSONFeature } from 'react-map-gl'
import { Layer, Popup, Source, useMap } from 'react-map-gl'
import type { MapStyleValue } from '~/atoms'
import {
  aHoverFeature,
  aMapStyle,
  aVisibleGeographies,
  useAtomValue,
} from '~/atoms'
import { useGeographies } from '~/hooks/map'
import type { Client } from '~/models'

type GeographiesLayerProps = {
  client: Client
}

type MouseEvent = mapboxgl.MapMouseEvent & {
  features?: MapboxGeoJSONFeature[] | undefined
} & mapboxgl.EventData

type SelectedGeography = {
  id: string
  type: string
  label: string
  latitude: number
  longitude: number
  data: Record<string, any>
}

const colors = [
  '#9333ea',
  '#a16207',
  '#4338ca',
  '#b91c1c',
  '#0284c7',
  '#3f3f46',
  '#0d9488',
] as const

function getLayerStyles(
  index: number,
  type: string,
  beforeId: string | undefined,
  style: MapStyleValue
) {
  const color = colors[index % colors.length]

  const layerStyle: LayerProps = {
    type: 'line',
    beforeId: beforeId,
    paint: {
      'line-color': color,
      'line-width': style === 'satellite' ? 2 : 1,
    },
  }

  const layerStyleFill: LayerProps = {
    id: `geographies-fill-${type}`,
    interactive: true,
    beforeId: beforeId,
    type: 'fill',
    paint: {
      'fill-color': color,
      'fill-opacity': 0.05,
    },
  }

  return { layerStyle, layerStyleFill }
}

export default function MapGeographiesLayer({ client }: GeographiesLayerProps) {
  const { geographies } = useGeographies(client)
  const mapRef = useMap()
  const mapStyle = useAtomValue(aMapStyle)
  const visibleGeographies = useAtomValue(aVisibleGeographies)
  const [selectedGeography, setSelectedGeography] =
    useState<SelectedGeography | null>(null)
  const hoverFeature = useAtomValue(aHoverFeature)

  const filteredGeographies = useMemo(() => {
    return geographies.filter((geography) => {
      return visibleGeographies.includes(geography.type)
    })
  }, [geographies, visibleGeographies])

  const geographyStyles = useMemo(() => {
    const beforeId = mapRef.current?.getLayer('property-clusters')
      ? 'property-clusters'
      : 'z-index-2'

    return Object.fromEntries(
      geographies.map((geography, index) => [
        geography.type,
        getLayerStyles(index, geography.type, beforeId, mapStyle),
      ])
    )
  }, [geographies, mapRef.current, mapStyle])

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!e.features || e.features.length === 0) return

      const [feature] = e.features

      const sameFeature =
        selectedGeography?.id &&
        feature.properties?.id === selectedGeography?.id

      if (sameFeature) {
        return
      }

      setSelectedGeography({
        ...feature.properties,
        data: feature.properties?.data
          ? JSON.parse(feature.properties?.data)
          : {},
        latitude: feature.properties!.latitude,
        longitude: feature.properties!.longitude,
      } as SelectedGeography)
    },
    [selectedGeography]
  )

  const onMouseOut = useCallback(() => {
    setSelectedGeography(null)
  }, [])

  useEffect(() => {
    if (mapRef.current && filteredGeographies.length > 0) {
      const map = mapRef.current.getMap()

      filteredGeographies.forEach((geography) => {
        map.on('mousemove', `geographies-fill-${geography.type}`, onMouseMove)
        map.on('mouseleave', `geographies-fill-${geography.type}`, onMouseOut)
      })
    }

    return () => {
      if (mapRef.current && filteredGeographies.length > 0) {
        const map = mapRef.current.getMap()

        filteredGeographies.forEach((geography) => {
          map.off(
            'mousemove',
            `geographies-fill-${geography.type}`,
            onMouseMove
          )
          map.off(
            'mouseleave',
            `geographies-fill-${geography.type}`,
            onMouseOut
          )
        })
      }
    }
  }, [mapRef.current, filteredGeographies])

  return (
    <>
      {filteredGeographies.map((geography) => {
        const { layerStyle, layerStyleFill } = geographyStyles[geography.type]
        return (
          <Fragment key={geography.type}>
            <Source
              id={`geographies-${geography.type}`}
              type="geojson"
              data={geography.geojson}>
              <Layer {...layerStyle} />
              <Layer {...layerStyleFill} />
            </Source>
            {selectedGeography && !hoverFeature && (
              <Popup
                key={selectedGeography.id}
                latitude={selectedGeography.latitude}
                longitude={selectedGeography.longitude}
                closeButton={false}
                onClose={() => setSelectedGeography(null)}
                className="disable-pointer-events z-30">
                <span className="font-semibold">{selectedGeography.label}</span>
                {selectedGeography.data && (
                  <dl className="mt-3 text-sm capitalize">
                    {Object.entries(selectedGeography.data).map(
                      ([key, value]) => {
                        if (key === 'state_abbr' || key.includes('_fp')) return
                        return (
                          <div
                            key={key}
                            className="mb-1 grid grid-cols-3 gap-2">
                            <dt className="col-span-1 font-medium leading-tight">
                              {key}:
                            </dt>
                            <dd className="col-span-2 capitalize">{value}</dd>
                          </div>
                        )
                      }
                    )}
                  </dl>
                )}
              </Popup>
            )}
          </Fragment>
        )
      })}
    </>
  )
}
